/** Canvas zoom: a scale factor, or "fit" to shrink the page to the canvas width. */
export type Zoom = number | "fit";

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2;

/** Stops the zoom buttons and shortcuts step through. */
export const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2];

/** Choices offered in the zoom menu. */
export const ZOOM_PRESETS = [0.5, 0.75, 1, 1.5, 2];

export function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));
}

/** The scale actually applied, given the "fit" scale the canvas measured. */
export function effectiveZoom(zoom: Zoom, fitZoom: number): number {
  return zoom === "fit" ? fitZoom : zoom;
}

/** The scale that fits a page of `pageWidth` into `availableWidth`, never enlarging it. */
export function fitZoomFor(availableWidth: number, pageWidth: number): number {
  if (availableWidth <= 0 || pageWidth <= 0) return 1;
  return Math.max(MIN_ZOOM, Math.min(1, Math.floor((availableWidth / pageWidth) * 100) / 100));
}

/** The next step up (or down) from the current scale. */
export function stepZoom(current: number, direction: 1 | -1): number {
  const next =
    direction === 1
      ? ZOOM_STEPS.find((step) => step > current + 0.001)
      : [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001);
  return next ?? (direction === 1 ? MAX_ZOOM : MIN_ZOOM);
}

export function formatZoom(value: number): string {
  return `${Math.round(value * 100)}%`;
}
